import { useCallback, useContext, useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import Loading from '~/components/Loading'
import Table from '~/components/Table'
import { FeedbackContext } from '~/components/feedback'
import {
  deleteAdSizesService,
  getAdSizesService
} from '~/modules/retailMedia/services/ad-size'

import { QUERY_KEY_GET_AD_SIZES } from '../constants'
import { useAdSizeForm } from '../Form/hook/useAdSizeForm'
import { adSizeTableSchema } from './schema'

const AdSizeTable = () => {
  const { formData } = useAdSizeForm()
  const { toast } = useContext(FeedbackContext)

  const { status, data } = useQuery({
    queryKey: QUERY_KEY_GET_AD_SIZES,
    queryFn: async () => {
      const res = await getAdSizesService()

      return res.data?.data
    }
  })

  /**
   * Handle delete
   */
  const queryClient = useQueryClient()

  const { isPending: isPendingDelete, mutateAsync: mutateAsyncDelete } =
    useMutation({
      mutationFn: deleteAdSizesService,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEY_GET_AD_SIZES
        })

        toast.success(t('rm:adsize.remove.messages.success.message'))
      },
      onError: () => {
        toast.critical(t('rm:adsize.remove.messages.error.message'))
      }
    })

  const handleDelete = useCallback(
    async (id: string) => {
      await mutateAsyncDelete(id)
    },
    [mutateAsyncDelete]
  )

  /**
   * Schema
   */
  const schema = useMemo(
    () =>
      adSizeTableSchema({
        formData: formData,
        onDelete: handleDelete,
        isPending: isPendingDelete
      }),
    [formData, handleDelete, isPendingDelete]
  )

  return (
    <div className="col p-0">
      <Loading status={status === 'pending'} />

      <Table schema={schema} data={data} sideBorder striped />
    </div>
  )
}

export default AdSizeTable
